import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Card } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Badge } from "../../../components/ui/badge";
import {
  FileSpreadsheet,
  FileText,
  Settings,
  Upload,
  X,
  Loader2,
} from "lucide-react";
import { useCreateControl } from "../../../hooks/useControlsV2";
import { useCreateCollabSession } from "../../../hooks/useCollabControl";
import { Progress } from "../../../components/ui/progress";
import { useAuth } from "../../../contexts/authContext";
import {
  getSystemDisplayName,
  getSystemIconPath,
} from "../../../lib/systemOfRecord";
import { validateFilesWithToast } from "../../../lib/fileUploadValidation";
import { CONTROL_BUILD_MAX_FILE_SIZE_BYTES } from "../../../lib/fileTypes";
import { ExternalControlPickerDialog } from "../../../components/ExternalControlPickerDialog";
import { type ExternalControl } from "../../../models/audit/externalControl";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { RcmUploadCard } from "../rcmImport/components/RcmUploadCard";

export const CreateControlPageV2 = () => {
  const navigate = useNavigate();
  const { workspace } = useAuth();
  const hasIntegration = !!workspace?.systemOfRecord;
  const systemDisplayName = workspace?.systemOfRecord
    ? getSystemDisplayName(workspace.systemOfRecord.source)
    : "";

  // Manual flow
  const { createControl, loading: loadingCreateControl } = useCreateControl();

  // AI flow
  const {
    createSession: createCollabSession,
    loading: loadingCollabSession,
    presignedUploadState,
    cancelUpload,
  } = useCreateCollabSession();
  const [controlDefFiles, setControlDefFiles] = useState<File[]>([]);
  const [additionalControlDetails, setAdditionalControlDetails] =
    useState<string>("");
  const [pastWorkPaperFiles, setPastWorkPaperFiles] = useState<File[]>([]);
  const controlDefInputRef = useRef<HTMLInputElement | null>(null);
  const pastWorkpaperInputRef = useRef<HTMLInputElement | null>(null);
  const [dragOverLeft, setDragOverLeft] = useState(false);
  const [dragOverRight, setDragOverRight] = useState(false);
  const [selectedMode, setSelectedMode] = useState<"manual" | "rcm" | "ai">(
    "ai",
  );
  const [selectedExternalControl, setSelectedExternalControl] =
    useState<ExternalControl | null>(null);
  const [pickerDialogOpen, setPickerDialogOpen] = useState(false);

  const onCreateManually = async () => {
    const control = await createControl(
      "New Control",
      "Control description",
      "itgc",
    );
    if (control) {
      navigate(`/audit/controls/v2/${control.id}`);
    }
  };

  const handleExternalControlSelected = (control: ExternalControl) => {
    setSelectedExternalControl(control);
    setControlDefFiles([]);
  };

  const onGenerate = async () => {
    if (!selectedExternalControl && controlDefFiles.length === 0) return;
    const sessionId = await createCollabSession({
      controlDefFiles: controlDefFiles.length > 0 ? controlDefFiles : undefined,
      pastWorkPaperFiles:
        pastWorkPaperFiles.length > 0 ? pastWorkPaperFiles : undefined,
      additionalControlDetails,
      externalRef: selectedExternalControl?.externalRef,
    });
    if (sessionId) {
      navigate(`/audit/controls/v2/collab/${sessionId}`);
    }
  };

  const removeControlDefFile = (index: number) => {
    setControlDefFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const removePastWorkPaperFile = (index: number) => {
    setPastWorkPaperFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="h-screen overflow-y-auto w-full p-6 max-w-[1100px] mx-auto overflow-x-hidden">
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">
          Create a new control
        </h1>
      </div>

      {/* Setup Manually selectable card */}
      <div
        role="button"
        onClick={() => setSelectedMode("manual")}
        className={`w-full text-left mb-4 transition border rounded-xl p-5 cursor-pointer hover:bg-stone-50 ${
          selectedMode === "manual" ? "ring-2 ring-stone-300" : ""
        }`}
      >
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 bg-stone-100 rounded-lg flex items-center justify-center">
            <Settings size={20} />
          </div>
          <div>
            <div className="text-lg font-semibold">Setup Manually</div>
            <div className="text-sm text-stone-600">
              Create your test template from scratch with full control over
              procedures and attributes.
            </div>
          </div>
        </div>
      </div>

      {/* RCM spreadsheet import selectable card — the upload step renders
          inline below (like AI Generate's dropzones); Continue then enters
          the column-mapping wizard. */}
      <div
        role="button"
        onClick={() => setSelectedMode("rcm")}
        className={`w-full text-left mb-4 transition border rounded-xl p-5 cursor-pointer hover:bg-stone-50 ${
          selectedMode === "rcm" ? "ring-2 ring-stone-300" : ""
        }`}
      >
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 bg-stone-100 rounded-lg flex items-center justify-center">
            <FileSpreadsheet size={20} />
          </div>
          <div>
            <div className="text-lg font-semibold">Import from RCM</div>
            <div className="text-sm text-stone-600">
              Bulk import controls from an RCM spreadsheet by mapping its
              columns.
            </div>
          </div>
        </div>
      </div>

      {/* AI Generate selectable card */}
      <div
        role="button"
        onClick={() => setSelectedMode("ai")}
        className={`w-full text-left mb-4 border rounded-xl p-5 cursor-pointer hover:bg-stone-50 ${
          selectedMode === "ai" ? "ring-2 ring-stone-300" : ""
        }`}
      >
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 bg-stone-100 rounded-lg flex items-center justify-center">
            <FileText size={20} />
          </div>
          <div>
            <div className="text-lg font-semibold">AI Generate</div>
            <div className="text-sm text-stone-600">
              Review and refine the AI-generated plan before creating the
              control.
            </div>
          </div>
        </div>
      </div>

      {/* RCM upload step - only when RCM is selected */}
      {selectedMode === "rcm" && <RcmUploadCard />}

      {/* Dropzones - only when AI is selected */}
      {selectedMode === "ai" && (
        <div className="flex flex-col gap-6">
          <div className="grid w-full grid-cols-1 md:grid-cols-2 gap-6 mt-2 min-w-0 max-w-full box-border overflow-hidden">
            {/* Control definition dropzone (Left) */}
            <Card
              className={`p-6 border-2 border-dashed min-h-[260px] min-w-0 w-full box-border overflow-hidden flex items-center justify-center ${
                dragOverLeft
                  ? "bg-stone-50 border-stone-400"
                  : "border-stone-300"
              }`}
              onDragEnter={(e) => {
                e.preventDefault();
                setDragOverLeft(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOverLeft(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setDragOverLeft(false);
              }}
              onDrop={async (e) => {
                e.preventDefault();
                setDragOverLeft(false);
                const files = Array.from(e.dataTransfer.files || []);
                const validFiles = await validateFilesWithToast(
                  files,
                  CONTROL_BUILD_MAX_FILE_SIZE_BYTES,
                );
                if (validFiles.length > 0) {
                  setSelectedExternalControl(null);
                  setControlDefFiles((prev) => [...prev, ...validFiles]);
                }
              }}
            >
              <div className="text-center w-full">
                {selectedExternalControl ? (
                  <>
                    <div className="flex justify-center mb-4">
                      {workspace?.systemOfRecord && (
                        <img
                          src={getSystemIconPath(
                            workspace.systemOfRecord.source,
                          )}
                          alt={systemDisplayName}
                          className="h-10 w-10"
                        />
                      )}
                    </div>
                    <div className="font-medium mb-3">
                      1. Control from {systemDisplayName}
                    </div>
                    <div className="mb-3 max-w-[90%] mx-auto">
                      <div className="flex items-center justify-between gap-2 text-sm text-stone-700 bg-stone-50 px-3 py-2 rounded">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="truncate font-medium">
                            {selectedExternalControl.name}
                          </span>
                          {selectedExternalControl.idCode && (
                            <Badge
                              variant="outline"
                              className="text-xs flex-shrink-0"
                            >
                              {selectedExternalControl.idCode}
                            </Badge>
                          )}
                        </div>
                        <button
                          type="button"
                          className="inline-flex items-center justify-center h-5 w-5 rounded hover:bg-stone-200 flex-shrink-0"
                          aria-label="Remove selection"
                          onClick={() => setSelectedExternalControl(null)}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => setPickerDialogOpen(true)}
                    >
                      Change selection
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="flex justify-center mb-4">
                      <Upload className="h-10 w-10 text-stone-400" />
                    </div>
                    <div className="font-medium mb-2">
                      1. Upload the control detail docs
                    </div>
                    <div className="max-w-md mx-auto text-left text-xs text-stone-700 bg-stone-100 rounded-md p-3 mb-3">
                      <ul className="list-disc pl-4 space-y-1">
                        <li>Control testing and walkthrough documentation</li>
                        <li>Optro control detail page printed as PDF</li>
                      </ul>
                    </div>
                    {controlDefFiles.length > 0 ? (
                      <div className="mb-3 max-w-[90%] mx-auto space-y-1">
                        {controlDefFiles.map((file, index) => (
                          <div
                            key={`${file.name}-${index}`}
                            className="flex items-center justify-between gap-2 text-xs text-stone-600 bg-stone-50 px-2 py-1 rounded"
                          >
                            <div className="truncate" title={file.name}>
                              {file.name}
                            </div>
                            <button
                              type="button"
                              className="inline-flex items-center justify-center h-5 w-5 rounded hover:bg-stone-200 flex-shrink-0"
                              aria-label="Remove file"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeControlDefFile(index);
                              }}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : null}
                    <Button
                      className="gap-2"
                      onClick={() => controlDefInputRef.current?.click()}
                      variant="outline"
                    >
                      <Upload className="h-4 w-4" /> Choose Files
                    </Button>
                    <Input
                      ref={controlDefInputRef}
                      id="controlDef"
                      type="file"
                      multiple
                      className="hidden"
                      onChange={async (e) => {
                        const files = Array.from(e.target.files || []);
                        const validFiles = await validateFilesWithToast(
                          files,
                          CONTROL_BUILD_MAX_FILE_SIZE_BYTES,
                        );
                        if (validFiles.length > 0) {
                          setSelectedExternalControl(null);
                          setControlDefFiles((prev) => [
                            ...prev,
                            ...validFiles,
                          ]);
                        }
                      }}
                    />
                    {hasIntegration && (
                      <>
                        <div className="flex items-center gap-3 my-4">
                          <div className="flex-1 border-t border-stone-300" />
                          <span className="text-xs text-stone-500">or</span>
                          <div className="flex-1 border-t border-stone-300" />
                        </div>
                        <Button
                          variant="outline"
                          className="gap-2"
                          onClick={() => setPickerDialogOpen(true)}
                        >
                          {workspace?.systemOfRecord && (
                            <img
                              src={getSystemIconPath(
                                workspace.systemOfRecord.source,
                              )}
                              alt={systemDisplayName}
                              className="h-4 w-4"
                            />
                          )}
                          Pick from {systemDisplayName}
                        </Button>
                      </>
                    )}
                  </>
                )}
              </div>
            </Card>

            {/* Past workpaper dropzone (Right) */}
            <Card
              className={`p-6 border-2 border-dashed min-h-[260px] min-w-0 w-full box-border overflow-hidden flex items-center justify-center ${
                dragOverRight
                  ? "bg-stone-50 border-stone-400"
                  : "border-stone-300"
              }`}
              onDragEnter={(e) => {
                e.preventDefault();
                setDragOverRight(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOverRight(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setDragOverRight(false);
              }}
              onDrop={async (e) => {
                e.preventDefault();
                setDragOverRight(false);
                const files = Array.from(e.dataTransfer.files || []);
                const validFiles = await validateFilesWithToast(
                  files,
                  CONTROL_BUILD_MAX_FILE_SIZE_BYTES,
                );
                if (validFiles.length > 0) {
                  setPastWorkPaperFiles((prev) => [...prev, ...validFiles]);
                }
              }}
            >
              <div className="text-center w-full">
                <div className="flex justify-center mb-4">
                  <Upload className="h-10 w-10 text-stone-400" />
                </div>
                <div className="font-medium mb-2">
                  2. Upload a past workpaper (Optional)
                </div>
                {pastWorkPaperFiles.length > 0 ? (
                  <div className="mb-3 max-w-[90%] mx-auto space-y-1">
                    {pastWorkPaperFiles.map((file, index) => (
                      <div
                        key={`${file.name}-${index}`}
                        className="flex items-center justify-between gap-2 text-xs text-stone-600 bg-stone-50 px-2 py-1 rounded"
                      >
                        <div className="truncate" title={file.name}>
                          {file.name}
                        </div>
                        <button
                          type="button"
                          className="inline-flex items-center justify-center h-5 w-5 rounded hover:bg-stone-200 flex-shrink-0"
                          aria-label="Remove file"
                          onClick={(e) => {
                            e.stopPropagation();
                            removePastWorkPaperFile(index);
                          }}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
                <Button
                  className="gap-2"
                  onClick={() => pastWorkpaperInputRef.current?.click()}
                  variant="outline"
                >
                  <Upload className="h-4 w-4" /> Choose Files
                </Button>
                <Input
                  ref={pastWorkpaperInputRef}
                  id="pastWorkPaper"
                  type="file"
                  multiple
                  className="hidden"
                  onChange={async (e) => {
                    const files = Array.from(e.target.files || []);
                    const validFiles = await validateFilesWithToast(
                      files,
                      CONTROL_BUILD_MAX_FILE_SIZE_BYTES,
                    );
                    if (validFiles.length > 0) {
                      setPastWorkPaperFiles((prev) => [...prev, ...validFiles]);
                    }
                  }}
                />
              </div>
            </Card>
          </div>
          <div>
            <div>
              <Label>Additional Control Details (Optional)</Label>
              <Textarea
                placeholder="Enter additional control details"
                value={additionalControlDetails}
                onChange={(e) => setAdditionalControlDetails(e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Presigned upload progress */}
      {presignedUploadState.isUploading && (
        <div className="mt-4 rounded-lg border border-stone-200 bg-stone-50 p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sm text-stone-700">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>
                Uploading files... {presignedUploadState.overallProgress}%
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={cancelUpload}
              className="text-xs text-stone-500 hover:text-stone-700"
            >
              Cancel
            </Button>
          </div>
          <Progress
            value={presignedUploadState.overallProgress}
            className="h-2"
          />
          <div className="mt-2 space-y-1">
            {presignedUploadState.files.map((file) => (
              <div
                key={file.filename}
                className="flex items-center justify-between text-xs text-stone-500"
              >
                <span className="truncate max-w-[70%]">{file.filename}</span>
                <span>
                  {file.status === "error"
                    ? file.error || "Failed"
                    : `${file.progress}%`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Confirm CTA — RCM mode has its own Continue inside its card */}
      {selectedMode !== "rcm" && (
        <div className="mt-6 flex justify-end">
          <Button
            onClick={selectedMode === "manual" ? onCreateManually : onGenerate}
            disabled={
              loadingCreateControl ||
              loadingCollabSession ||
              presignedUploadState.isUploading ||
              (selectedMode === "ai" &&
                controlDefFiles.length === 0 &&
                !selectedExternalControl)
            }
            loading={
              selectedMode === "ai"
                ? loadingCollabSession
                : loadingCreateControl
            }
          >
            {selectedMode === "manual" ? "Create Control" : "Generate Control"}
          </Button>
        </div>
      )}

      {hasIntegration && workspace?.systemOfRecord && (
        <ExternalControlPickerDialog
          open={pickerDialogOpen}
          onOpenChange={setPickerDialogOpen}
          systemDisplayName={systemDisplayName}
          onControlSelected={handleExternalControlSelected}
        />
      )}
    </div>
  );
};

export default CreateControlPageV2;
